import { authService } from '@/features/auth/services/authService';
import {
    DailyScrumComment,
    DailyScrumCommentWithReplies,
} from '../../../common/types/databaseTypes';
import { supabase } from '@tickitacka/shared';

export interface CreateDailyScrumCommentParams {
    daily_scrum_item_id: string;
    content: string;
    parent_comment_id?: string;
}

export const dailyScrumCommentService = {
    /**
     * 댓글 작성자 ID 조회
     * 부모 댓글 작성자 ID 조회 할 때 사용(답글 알림 발송)
     * @param commentId 댓글 ID
     * @returns 댓글 작성자 ID 또는 null (조회 실패 시)
     */
    async getDailyScrumCommentRegisterIdById(
        commentId: string
    ): Promise<string | null> {
        try {
            const { data: comment, error } = await supabase
                .from('daily_scrum_comment')
                .select('register_id')
                .eq('id', commentId)
                .single();

            if (error || !comment) {
                console.error('댓글 정보 조회 실패:', error);
                return null;
            }

            return comment.register_id;
        } catch (error) {
            console.error('댓글 작성자 ID 조회 실패:', error);
            return null;
        }
    },

    /**
     * 데일리 스크럼 아이템에 새 댓글을 생성합니다
     * @param data 댓글 생성 파라미터
     * @returns 생성된 댓글 정보
     */
    async createDailyScrumComment(
        data: CreateDailyScrumCommentParams
    ): Promise<DailyScrumComment> {
        try {
            const user = await authService.getCurrentUser();
            if (!user) throw new Error('사용자가 로그인되어 있지 않습니다.');

            const insertData = {
                daily_scrum_item_id: data.daily_scrum_item_id,
                content: data.content,
                parent_comment_id: data.parent_comment_id || null,
                register_id: user.id,
                register_date: new Date().toISOString(),
                update_date: new Date().toISOString(),
                enable: true,
            };

            const { data: result, error } = await supabase
                .from('daily_scrum_comment')
                .insert(insertData)
                .select()
                .single();

            if (error) throw error;
            if (!result) throw new Error('댓글 생성에 실패했습니다.');

            return result as DailyScrumComment;
        } catch (error) {
            console.error('Error creating daily scrum comment:', error);
            throw error;
        }
    },

    /**
     * 데일리 스크럼 댓글을 수정합니다
     * @param commentId 댓글 ID
     * @param content 수정할 댓글 내용
     * @returns 수정된 댓글 정보
     */
    async updateDailyScrumComment(
        commentId: string,
        content: string
    ): Promise<DailyScrumComment> {
        try {
            const user = await authService.getCurrentUser();
            if (!user) throw new Error('사용자가 로그인되어 있지 않습니다.');

            const { data: result, error } = await supabase
                .from('daily_scrum_comment')
                .update({
                    content,
                    update_date: new Date().toISOString(),
                    update_id: user.id,
                })
                .eq('id', commentId)
                .select()
                .single();

            if (error) throw error;

            return {
                ...result,
                enable: result.enable ?? true,
            } as DailyScrumComment;
        } catch (error) {
            console.error(`Failed to update daily scrum comment:`, error);
            throw error;
        }
    },

    /**
     * 데일리 스크럼 댓글을 삭제합니다 (완전 삭제)
     * @param id 댓글 ID
     */
    async deleteDailyScrumComment(id: string): Promise<void> {
        try {
            const { error } = await supabase
                .from('daily_scrum_comment')
                .delete()
                .eq('id', id);

            if (error) throw error;
        } catch (error) {
            console.error(`Failed to delete daily scrum comment:`, error);
            throw error;
        }
    },
};
